import { CheckCircle2, ExternalLink, LogIn, Lock, MessageSquareText, Printer, Store, XCircle } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../../components/ui/sheet";
import { Skeleton } from "../../../components/ui/skeleton";
import { hora, reais } from "../lib/caixa";
import { conferenciaDoCaixa, dataPorExtenso, duracao, nomeDoCanal, resultadoDoCaixa } from "../lib/fechamento";
import { useDetalheCaixa } from "../hooks/useDetalheCaixa";
import Movimentacoes from "./Movimentacoes";
import { ESTILO_RESULTADO } from "./ResultadoCaixa";
import VendasPorForma from "./VendasPorForma";

const Titulo = ({ children }) => <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">{children}</h3>;

/** O resultado em grande: bateu, faltou ou sobrou, com contado × esperado. */
function Resultado({ caixa }) {
  const res = resultadoDoCaixa(caixa);
  const e = ESTILO_RESULTADO[res.tipo];
  return (
    <div className={`rounded-2xl border p-4 flex items-center gap-3 ${e.fundo}`} data-e2e="fechamento-resultado" data-resultado={res.tipo}>
      <e.icone className="w-9 h-9 shrink-0" />
      <div className="min-w-0">
        <p className="text-2xl font-black leading-tight">{e.texto(res.valor, res)}</p>
        {res.tipo === "bateu" && res.valor > 0 && <p className="text-sm text-foreground/80">Dentro da diferença aceitável da loja.</p>}
        {caixa.valor_fechamento_real != null && (
          <p className="text-sm text-foreground/80">Contado {reais(caixa.valor_fechamento_real)} · esperado {reais(caixa.valor_fechamento_esperado ?? caixa.valor_fechamento_real)}</p>
        )}
      </div>
    </div>
  );
}

/** Esperado × contado × diferença de cada forma, como o operador viu ao fechar. */
function Conferencia({ caixa }) {
  const formas = conferenciaDoCaixa(caixa);
  if (!formas) {
    return (
      <p className="text-sm text-muted-foreground rounded-xl border border-dashed p-3" data-e2e="conferencia-antiga">
        Este caixa foi fechado sem a conferência por forma de pagamento: só o total foi contado ({reais(caixa.valor_fechamento_real)} de {reais(caixa.valor_fechamento_esperado)} esperados).
      </p>
    );
  }
  return (
    <div className="rounded-xl border overflow-hidden" data-e2e="conferencia-formas">
      <div className="grid grid-cols-3 sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_6rem] gap-x-2 px-3 py-2 bg-muted/50 text-[11px] font-semibold text-muted-foreground uppercase">
        <span className="hidden sm:block">Forma</span><span className="text-right">Esperado</span><span className="text-right">Contado</span><span className="text-right">Diferença</span>
      </div>
      {formas.map((f) => {
        const auto = f.automatico || f.sem_forma;
        const dif = Number(f.diferenca) || 0;
        const bateu = f.bateu ?? Math.abs(dif) <= 1;
        return (
          <div key={f.forma} className="grid grid-cols-3 sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_6rem] gap-x-2 gap-y-1 px-3 py-2 border-t text-[13px] items-center tabular-nums">
            <span className="col-span-3 sm:col-span-1 font-medium flex items-center gap-1.5 min-w-0">
              {bateu ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
              <span className="truncate">{f.forma}</span>
              {f.gaveta && <span className="text-[10px] text-muted-foreground font-normal">(gaveta)</span>}
            </span>
            <span className="text-right">{reais(f.esperado)}</span>
            <span className="text-right">{auto ? <span className="text-[11px] text-muted-foreground">automático</span> : reais(f.contado)}</span>
            <span className={`text-right font-semibold ${Math.abs(dif) < 0.01 ? "text-muted-foreground" : dif < 0 ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`}>
              {Math.abs(dif) < 0.01 ? "—" : `${dif > 0 ? "+" : "−"}${reais(Math.abs(dif))}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Painel lateral com o fechamento de um caixa anterior, sem precisar imprimir (25/10/2026). */
export default function DetalheFechamento({ caixa, onClose, onReimprimir, onVerPedidos }) {
  const { resumo, movimentos, carregando, erro } = useDetalheCaixa(caixa);
  const est = resumo?.estatisticas || {};
  const modulos = [...(resumo?.vendas_por_modulo || [])].sort((a, b) => (b.total || 0) - (a.total || 0));
  const totalModulos = modulos.reduce((s, m) => s + (m.total || 0), 0) || 1;
  const fechou = caixa?.fechado_por || caixa?.usuario_nome || "—";
  return (
    <Sheet open={!!caixa} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col" data-e2e="detalhe-fechamento">
        {caixa && (
          <>
            <SheetHeader className="p-5 pb-3 border-b text-left">
              <SheetTitle className="text-xl">Caixa #{caixa.id}</SheetTitle>
              <SheetDescription className="first-letter:uppercase">{dataPorExtenso(caixa.opened_at)}</SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              <Resultado caixa={caixa} />

              {(caixa.justificativa || caixa.observacao) && (
                <div className="rounded-2xl border bg-card p-4 space-y-2" data-e2e="fechamento-motivo">
                  {caixa.justificativa && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><MessageSquareText className="w-3.5 h-3.5" />O que aconteceu</p>
                      <p className="mt-1 text-base italic">“{caixa.justificativa}”</p>
                      <p className="text-xs text-muted-foreground mt-1">— escrito por {fechou} ao fechar</p>
                    </div>
                  )}
                  {caixa.observacao && <p className="text-sm"><span className="font-semibold">Observação:</span> {caixa.observacao}</p>}
                </div>
              )}

              <div>
                <Titulo>Turno</Titulo>
                <ol className="relative space-y-3 before:absolute before:left-[17px] before:top-2 before:bottom-2 before:w-px before:bg-border">
                  <li className="relative flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 z-10 bg-primary/10 text-primary"><LogIn className="w-4 h-4" /></span>
                    <p className="text-sm flex-1"><span className="font-semibold">Abriu às {hora(caixa.opened_at)}</span> · {caixa.usuario_nome || "—"}</p>
                    <span className="text-sm tabular-nums">{reais(caixa.valor_abertura)}<span className="text-[11px] text-muted-foreground"> de troco</span></span>
                  </li>
                  <li className="relative flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 z-10 bg-muted"><Lock className="w-4 h-4" /></span>
                    <p className="text-sm flex-1"><span className="font-semibold">Fechou às {hora(caixa.closed_at)}</span> · {fechou}</p>
                    <span className="text-sm text-muted-foreground">{duracao(caixa) ? `durou ${duracao(caixa)}` : ""}</span>
                  </li>
                </ol>
              </div>

              <div>
                <Titulo>Conferência por forma de pagamento</Titulo>
                <Conferencia caixa={caixa} />
              </div>

              {carregando ? (
                <div className="space-y-3" data-e2e="detalhe-carregando">
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-[72px] rounded-xl" />)}</div>
                  <Skeleton className="h-40 rounded-2xl" />
                  <Skeleton className="h-40 rounded-2xl" />
                </div>
              ) : erro ? (
                <p className="text-sm text-red-600">Não deu para carregar as vendas deste caixa. Feche e abra de novo.</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    {[["Vendido", reais(est.total_vendas)], ["Vendas", est.total_pedidos || 0], ["Ticket médio", reais(est.ticket_medio)]].map(([rotulo, valor]) => (
                      <div key={rotulo} className="rounded-xl border bg-card p-2.5 sm:p-3 min-w-0">
                        <p className="text-[11px] text-muted-foreground">{rotulo}</p>
                        <p className="text-sm sm:text-lg font-black tabular-nums">{valor}</p>
                      </div>
                    ))}
                  </div>

                  <VendasPorForma resumo={resumo} />

                  {modulos.length > 0 && (
                    <section className="rounded-2xl border bg-card p-4 sm:p-5" data-e2e="vendas-por-canal">
                      <h3 className="font-bold flex items-center gap-2 mb-3"><Store className="w-4 h-4 text-primary" />Vendas por canal</h3>
                      <ul className="space-y-3">
                        {modulos.map((m) => (
                          <li key={m.modulo}>
                            <div className="flex items-baseline gap-2 text-sm">
                              <span className="font-medium">{nomeDoCanal(m.modulo)}</span>
                              <span className="text-xs text-muted-foreground">{m.qtd}×</span>
                              <span className="ml-auto font-semibold tabular-nums">{reais(m.total)}</span>
                            </div>
                            <div className="h-2 rounded-full bg-muted mt-1 overflow-hidden"><div className="h-full rounded-full bg-primary/70" style={{ width: `${((m.total || 0) / totalModulos) * 100}%` }} /></div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <Movimentacoes movimentos={movimentos} onVerPedidos={() => onVerPedidos(caixa.id)} />
                </>
              )}
            </div>

            <div className="border-t p-4 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => onReimprimir(caixa)} data-e2e="detalhe-imprimir"><Printer className="w-4 h-4 mr-2" />Imprimir</Button>
              <Button className="flex-1" onClick={() => onVerPedidos(caixa.id)} data-e2e="detalhe-pedidos"><ExternalLink className="w-4 h-4 mr-2" />Ver pedidos</Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
